import type { $Fetch, FetchContext, FetchError } from "ofetch";
import { useAuth } from "@magnit/util-auth/src/composables";
import type {
  IAuthorizeUserBody,
  IFetchOTPCodeBody,
  IVerifyOTPCodeBody,
} from "@magnit/util-auth/src/types";
import { useNuxtApp, useRuntimeConfig } from "nuxt/app";
import {
  fetchLogin,
  fetchCode,
  fetchCheckCode,
  fetchRefreshTokens,
  fetchProfile,
  fetchRegister,
  fetchCheckUserProfile,
  fetchExchangeExternalToken, HttpCodes, ErrorCodes,
} from "../api";
import type { IGenerateTokenResponse } from "../typings";
import { IProfileIncompleteCookieEnum } from "../typings/cookies";
import useAuthCookies from "./useAuthCookies";
import useSinglePromise from "./useSinglePromise";

export default (options?: { skipLifeCycleHooks?: boolean; }) => {
  const { $transport } = useNuxtApp();
  const { public: publicConfig } = useRuntimeConfig();
  const aud = String(publicConfig.aud || "");
  const antiFraud = Boolean(publicConfig.antiFraud ? publicConfig.antiFraud === "true" : true);
  const yandexCaptcha = String(publicConfig.yandexCaptcha || "");
  const kfpCid = String(publicConfig.kfpCid) || undefined;
  const {
    cookieJWT,
    cookieKFP,
    cookieMagnitCode,
    refreshToken,
    profileIncompleteState,
  } = useAuthCookies();

  const refreshTokens = useSinglePromise(async () => {
    const options = {
      retry: 0,
      onResponse: (ctx: FetchContext<IGenerateTokenResponse>) => {
        const status = ctx.response?.status ?? -1;
        if (status === 401 || status === 400) {
          cookieJWT.value = null;
        }
      },
    };
    const response = await fetchRefreshTokens(aud, refreshToken.value || "", options);
    return response;
  });

  const checkUserProfile = async () => {
    try {
      await fetchCheckUserProfile();
      profileIncompleteState.value = IProfileIncompleteCookieEnum.valid;
    } catch (e) {
      const error = e as FetchError;
      if (
        error?.statusCode && error.statusCode === HttpCodes.UnprocessableEntity) {
        if (
          error?.data?.code === ErrorCodes.IncompleteProfileInfo) {
          profileIncompleteState.value = IProfileIncompleteCookieEnum.incomplete;
        } else {
          profileIncompleteState.value = IProfileIncompleteCookieEnum.valid;
        }
      }
    }
  };

  return useAuth({
    transport: $transport as $Fetch,

    cookieJWT,
    cookieKFP,
    cookieMagnitCode,

    aud,
    antiFraud,
    captchaKey: yandexCaptcha,
    kfpCid,

    authorizeUser: (b: IAuthorizeUserBody) => fetchLogin(aud, b.magnitIDCode),
    fetchOtpCodeByPhone: (b: IFetchOTPCodeBody) => fetchCode(aud, b.captchaToken, b.phoneNumber),
    verifyOtpCode: (b: IVerifyOTPCodeBody) =>
      fetchCheckCode(aud, b.code, b.phoneNumber, b.attemptId),
    refreshTokens,
    fetchProfile,
    register: fetchRegister,
    checkUserProfile,
    exchangeExternalToken: fetchExchangeExternalToken,

    skipLifeCycleHooks: options?.skipLifeCycleHooks ?? false,
  });
};
